import React, {useState, useEffect} from 'react';
import { useQuery } from '@apollo/client';
import DataState from 'app/src/components/DataState';
import { View, Text, Pressable } from 'react-native';
import { ASSET } from './queries';
import { useParams } from 'react-router-dom';
import { AssetsShowContainer, AssetDetailsContainer, SaleDetailsContainer } from './elements';
import {
  ContentContainer,
  LinkContainer,
  LinkSpace,
  MainContainer,
  TableContainer,
  PageTitle,
  TabButtonContainer,
  TabButton,
  TabText,
} from '../../elements';
import styled from 'styled-components/native';
import webStyled from 'styled-components';
import { elGray, elLightLightGray } from '../../styles/colors';
import formatAssetName from '../../utils/formatAssetName';
import Modal from 'app/src/elements/Modal';
import formatAddress from '../../utils/addressFormatter';
import DataTable from '@preflighttech/preflight-tables';
import { Link } from 'app/src/utils/routing';

import ResourceNotifications from 'app/src/components/Notifications/Resource';
import { currency } from 'app/src/components/Sales/Process';
import { numberWithDelimiter } from 'app/src/utils/stringHelpers';

import Fees from 'app/src/components/Fees';
import SaleHistory from './SaleHistory';

import formattedLocation from './formattedLocation';
import { formattedConsignmentLocation } from './formattedLocation';
import SubNav from './SubNav';
import moment from 'moment';

const UpperRow = styled(View)`
  flex-direction: row;
  justify-content: space-between;
`;

const LowerRow = styled(View)`
  flex-direction: row;
  margin-top: auto;
`;

const NotesContainer = styled(View)`
  background-color: ${elGray};
  display: flex;
  flex: 1;
  border-radius: 5px;
  padding: 10px;
`;

const Column = styled(View)`
`;

const Field = styled(View)`
  flex-direction: row;
`;

const Label = styled(Text)`
  font-weight: 600;
  margin-right: 4px;
`;

const Value = styled(Text)`
  margin-right: 10px;
`;

const Photo = webStyled.img`
  height: 100%;
`;

const Asset = props => {
  const [activeTab, setActiveTab] = useState('Fees');
  const { id } = useParams();
  const { loading, error, data, refetch } = useQuery(
    ASSET, { variables: { id } }
  );

  const asset = data?.asset;

  if(loading || !asset) {
    return <DataState.Loading />;
  }

  const photoUrl = asset?.photos[0]?.url || asset?.soarrPhotoUrls[0];
  return (
    <View>
      <MainContainer>
        {asset && (<SubNav asset={asset} refetch={refetch} />)}
        <ContentContainer>
          <AssetsShowContainer>
            {asset?.soldOn &&
              <SaleDetailsContainer>
                <Text><b>{asset?.status}</b></Text>
                <LinkSpace />
                <Text>
                  <b>Sold On: </b>{moment(asset?.soldOn).format('MM/DD/YYYY')}
                </Text>
                <LinkSpace />
                <Text>
                  <b>Sale Price: </b>
                  {currency(parseFloat(asset?.soldAmount))}
                </Text>
                <LinkSpace />
                {asset?.soldSaleAsset?.sellerNet && asset?.soldSaleAsset.sellerAchOn &&
                  <View style={{display: 'flex', flexDirection: 'row', flex: 1}}>
                    <Text>
                      <b>ACH Amount: </b>{currency(parseFloat(asset?.soldSaleAsset?.sellerNet))}
                    </Text>
                    <LinkSpace />
                    <Text>
                      <b>ACH On: </b>
                      {moment(asset?.soldSaleAsset.sellerAchOn).format('MM/DD/YYYY')}
                    </Text>
                  </View>
                }
              </SaleDetailsContainer>
            }
            <AssetDetailsContainer>
              <View style={{flex: 1}}>
                {photoUrl && <Photo src={photoUrl} alt={asset?.stockNumber} />}
              </View>
              <View style={{flex: 5}}>
                <UpperRow>
                  <Column>
                    <Text><b>Stock #: </b>{asset?.stockNumber}</Text>
                    <Text><b>VIN: </b>{asset?.vin}</Text>
                    <Text>
                      <b>{asset?.seller.clientNumberLabel}: </b>
                      {asset?.clientNumber}
                    </Text>
                    <Text><b>Category: </b>{asset?.category}</Text>
                    <Text><b>Sub-category: </b>{asset?.subCategory}</Text>
                    <Field>
                      <Label>Mechanical Conditions:</Label>
                      <Value>{asset?.mechanicalCondition}</Value>
                      {asset && (
                        <ResourceNotifications
                          resource={asset}
                          field="mechanicalCondition"
                        />
                      )}
                    </Field>
                    <Field>
                      <Label>Expected Yield Price:</Label>
                      <Value>{currency(asset?.expectedYieldPrice)}</Value>
                      {asset && (
                        <ResourceNotifications
                          resource={asset}
                          field="expectedYieldPrice"
                        />
                      )}
                    </Field>
                    {asset?.uccExpiresOn ?
                      <Text>
                        <b>UCC/Title Expiration Date: </b>
                        {moment(asset?.uccExpiresOn).format('MM-DD-YY')}
                      </Text> :
                      null
                    }
                  </Column>
                  <Column>
                    <Text>
                      <b>Odometer: </b>
                      {asset?.miles > 0 && numberWithDelimiter(asset?.miles)}
                    </Text>
                    <Text>
                      <b>Date Secured: </b>
                      {asset?.securedOn ?
                        moment(asset.securedOn).format('M/D/YY') : ''}
                    </Text>
                    <Text>
                      <b>List Price: </b>
                      {currency(asset?.listPrice)}
                    </Text>
                    <Text>
                      <b>Location: </b>
                      {formattedLocation(asset, { lineBreak: true })}
                    </Text>
                    <Text>
                      <b>{`${asset?.consignmentMethod}: `}</b>
                      {formattedConsignmentLocation(asset, {lineBreak: true})}
                    </Text>
                    <Text>
                      <b>Drivable: </b>
                      {asset?.drivable ? 'Yes' : 'No'}
                    </Text>
                  </Column>
                  <Column>
                    <Text><b>Engine Specifications</b></Text>
                    <Text>Manufacturer: {asset?.engineMfr}</Text>
                    <Text>Engine Type: {asset?.engineType}</Text>
                    <Text>Engine HP: {asset?.horsepower}</Text>
                  </Column>
                  <Column>
                    <Text><b>Drivetrain Specifications</b></Text>
                    <Text>Transmission: {asset?.transmission}</Text>
                    <Text>Axel: {asset?.axle}</Text>
                    <Text>Ratio: {asset?.ratio}</Text>
                    <Text>Wheelbase: {asset?.wheelBase}</Text>
                  </Column>
                  <Column>
                    <Text>Front Wheels: {asset?.wheelsFront}</Text>
                    <Text>Rear Wheels: {asset?.wheelsRear}</Text>
                    <Text>Sleeper: {asset?.sleeper}</Text>
                    <Text>Color: {asset?.color}</Text>
                  </Column>
                </UpperRow>
                <UpperRow>
                  <Column style={{flexDirection: 'row', gap: 10}}>
                    <Text>
                      <b>Inspection: </b>
                      {asset?.inspectionUrl ?
                        <View>
                          <a
                            href={asset?.inspectionUrl}
                            target="_blank"
                            rel="nofollow"
                          >
                            {asset?.inspectionUrl}
                          </a>
                        </View> :
                        'None Entered'
                      }
                    </Text>
                    <ResourceNotifications
                      resource={asset}
                      field="inspectionUrl"
                    />
                  </Column>
                </UpperRow>
                <LowerRow>
                  <View>
                    <Text>
                      {asset?.designation}
                    </Text>
                  </View>
                  <NotesContainer>
                    <Text>
                      {asset?.notes}
                    </Text>
                    </NotesContainer>
                </LowerRow>
              </View>
            </AssetDetailsContainer>
          </AssetsShowContainer>
          <TabButtonContainer>
            <TabButton
              onPress={() => setActiveTab('Fees')}
              active={'Fees' === activeTab}
            >
              <TabText active={'Fees' === activeTab}>Fees</TabText>
            </TabButton>
            <TabButton
              onPress={() => setActiveTab('SaleHistory')}
              active={'SaleHistory' === activeTab}
            >
              <TabText active={'SaleHistory' === activeTab}>
                Sale and Bid History
              </TabText>
            </TabButton>
            <TabButton
              onPress={() => setActiveTab('Notes/Documents')}
              active={'Notes/Documents' === activeTab}
            >
              <TabText active={'Notes/Documents' === activeTab}>
                Notes/Documents
              </TabText>
            </TabButton>
          </TabButtonContainer>
          <TableContainer>
            {'Fees' === activeTab && <Fees asset={asset} />}
            {'SaleHistory' === activeTab && <SaleHistory asset={asset} />}
            {'Notes/Documents' === activeTab &&
              <View
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'space-around',
                  margin: '20px'
                }}
              >
                <View style={{flexDirection: 'column', flexBasis: '100%', flex: 1}}>
                  <b>Asset Notes:</b>
                  <View
                    style={{marginLeft: '20px'}}
                  >
                    {asset.sellerNotes}
                  </View>
                </View>
                <View style={{flexDirection: 'column', flexBasis: '100%', flex: 1}}>
                  <b>Documents:</b>
                  {asset.sellerDocuments?.map(document => {
                    return (
                      <View
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          marginLeft: '20px',
                          marginBottom: '10px'
                        }}
                      >
                        <Link to={document.url} target='_blank'>
                          <Text>
                            {document.filename}
                          </Text>
                        </Link>
                        <View
                          style={{
                            display: 'flex',
                            flexDirection: 'row',
                            marginLeft: '20px',
                            marginTop: '2px'
                          }}
                        >
                          <Text style={{marginRight: '5px'}}><b>Notes:</b></Text>
                          {
                            asset.sellerDocumentNotes.map(note => {
                              if(note.attachmentId === document.id) {
                                return <Text>{note.body}</Text>
                              }
                            })
                          }
                        </View>
                      </View>
                    )
                  })}
                </View>
              </View>
            }
          </TableContainer>
        </ContentContainer>
      </MainContainer>
    </View>
  )
};

export default Asset;
